import { Component, computed, input, OnInit, Signal } from '@angular/core';
import {
  TableFilterEvent,
  TableSortEvent,
} from '../../../shared/components/table/table.component';
import { fromStore } from 'angular/app/core/store';
import { map, Observable, take, takeUntil } from 'rxjs';
import {
  Booking,
  BookingStatus,
  BookingStatusHumanized,
  RateType,
} from '../../../core/models/entities/booking';
import { Store } from '@ngrx/store';
import { BookingDetailsModalComponent } from '../booking-details-modal/booking-details-modal.component';
import { ModalService } from '../../../core/services/modal.service';
import { fadeAnimation } from '../../../core/animations/fade.animation';
import {
  BookingsScope,
  BookingsState,
} from '../../../core/store/bookings/bookings.state';
import { bookingsActions } from 'angular/app/core/store/bookings';
import { isSameDay, parseISO } from 'date-fns';
import {
  selectAllRooms,
  selectBookingsFilter,
} from '../../../core/store/reducers';
import { InputOptionsHelper } from '../../../core/helpers/input-options.helper';
import { UntypedFormControl } from '@angular/forms';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { selectRoomById } from '../../../core/store/rooms/rooms.reducer';
import { Room } from '../../../core/models/entities/room';

@Component({
  selector: 'app-bookings-list-view',
  animations: [fadeAnimation],
  template: `
    <app-table
      [data]="bookings()"
      [loading]="loadingBookings()"
      [trackByFn]="trackByBookingId"
      emptyStateTitle="Aucune réservation trouvée"
      emptyStateMessage="Aucune réservation ne correspond aux critères de votre recherche."
      emptyStateIcon="far fa-calendar"
      [showPagination]="true"
      [currentPage]="page$ | async"
      [pageSize]="25"
      [totalItems]="collectionSize$ | async"
      (pageChanged)="changePage($event.page)"
      (rowClicked)="openBooking($event)"
      (filterChanged)="onFilterChanged($event)"
      (sortChanged)="onSortChanged($event)"
      [currentSort]="currentSort()"
    >
      <!-- Reference Column -->
      <app-table-column
        field="reference"
        header="Réf."
        width="130px"
        wordBreak="break-all"
      >
        <ng-template let-booking>
          {{ booking.reference }}
        </ng-template>
      </app-table-column>

      <!-- Reservation Column -->
      <app-table-column
        field="name"
        header="Réservation"
        width="300px"
        [showLabelOnMobile]="false"
        [alwaysVisibleOnMobile]="true"
      >
        <ng-template let-booking>
          @if (booking.is_public) {
            <div>
              @if (booking.company) {
                <div class="font-bold">{{ booking.company }}</div>
              }
              <div [class.font-bold]="!booking.company">
                {{
                  booking.full_name === ' ' ? booking.name : booking.full_name
                }}
              </div>
            </div>
          } @else {
            <div>
              <div class="font-bold">{{ booking.name }}</div>
              <div>
                {{ booking.full_name }}
              </div>
            </div>
          }
        </ng-template>
      </app-table-column>

      <!-- Room Column -->
      <app-table-column
        field="room_name"
        header="Nom de la salle"
        [filterable]="true"
        [filterOptions]="roomsOptions$ | async"
        [filterValues]="roomsFilterValues$ | async"
      >
        <ng-template let-booking>
          {{ selectRoomById(booking?.room_id)()?.name }}
        </ng-template>
      </app-table-column>

      <!-- Date Column -->
      <app-table-column
        [alwaysVisibleOnMobile]="true"
        [showLabelOnMobile]="false"
        field="start"
        header="Date"
        [sortable]="true"
      >
        <ng-template let-booking>
          <div>
            {{ booking?.start | date: "EEE dd.MM.yyyy, HH'h'mm" }}
            @if (booking && isSameDayOrNextMorning(booking)) {
              <span>
                à {{ booking?.end | date: "HH'h'mm" }}
                @if (isNextMorning(booking)) {
                  <span class="text-gray-muted">(+1)</span>
                }
              </span>
            } @else {
              <div>
                {{ booking?.end | date: "EEE dd.MM.yyyy, HH'h'mm" }}
              </div>
            }
          </div>
        </ng-template>
      </app-table-column>

      <!-- Status Column -->
      <app-table-column
        field="status"
        header="Statut"
        [filterable]="true"
        [filterOptions]="statusOptions"
        [filterValues]="statusFilterValues$ | async"
        [defaultFilterValues]="[
          BookingStatus.Pending,
          BookingStatus.Confirmed,
          BookingStatus.PendingPayment,
        ]"
        [showLabelOnMobile]="false"
        [alwaysVisibleOnMobile]="true"
      >
        <ng-template let-booking>
          <app-badge
            [variant]="BookingStatusHumanized[booking.status].color"
            size="sm"
          >
            {{ BookingStatusHumanized[booking.status].label }}
          </app-badge>
        </ng-template>
      </app-table-column>

      <!-- Actions Column -->
      <app-table-actions>
        <ng-template let-booking>
          <app-table-action
            label="Ouvrir"
            (actionClick)="openBooking(booking)"
          ></app-table-action>
        </ng-template>
      </app-table-actions>
    </app-table>
  `,
  standalone: false,
})
export class BookingsListViewComponent
  extends DestroyableComponent
  implements OnInit
{
  scope = input<BookingsScope>();
  bookings: Signal<Booking[]> = computed(() =>
    this.scope()
      ? this.store
          .selectSignal(fromStore.selectAllBookings(this.scope()))()
          ?.filter((booking) => !!booking)
      : [],
  );
  loadingBookings: Signal<boolean> = computed(() =>
    this.scope()
      ? this.store.selectSignal(fromStore.selectBookingsLoading(this.scope()))()
      : true,
  );
  page$: Observable<number>;
  collectionSize$: Observable<number>;
  BookingStatusHumanized = BookingStatusHumanized;
  BookingStatus = BookingStatus;
  roomsOptions$ = InputOptionsHelper.selectOptions$(
    this.store
      .select(selectAllRooms)
      .pipe(map((rooms) => rooms.filter((room) => room.active))),
  );
  roomsFilterValues$: Observable<number[]>;
  statusOptions = Object.values(BookingStatus)
    .filter((value) => ![BookingStatus.PaymentFailed].includes(value))
    .map((value) => ({
      label: BookingStatusHumanized[value].label,
      value,
    }));
  statusFilterValues$: Observable<BookingStatus[]> = this.store
    .select(selectBookingsFilter(this.scope()))
    .pipe(map((bookingsFilter) => bookingsFilter.status));
  orderControl = new UntypedFormControl();

  currentSort = computed(() => {
    const bookingsState = this.store.selectSignal(
      fromStore.selectBookingsState,
    )();
    const order = bookingsState[this.scope()].order;
    if (order && order.length > 0) {
      const firstOrder = order[0];
      return {
        field: firstOrder.field,
        direction: firstOrder.order as 'asc' | 'desc' | null,
      };
    }
    return { field: '', direction: null };
  });

  constructor(
    private store: Store,
    private modalService: ModalService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.roomsFilterValues$ = this.store
      .select(fromStore.selectBookingsState)
      .pipe(
        map((bookingsState) => bookingsState[this.scope()].roomsFilter.ids),
      );
    this.statusFilterValues$ = this.store
      .select(fromStore.selectBookingsState)
      .pipe(
        map(
          (bookingsState) => bookingsState[this.scope()].bookingsFilter.status,
        ),
      );
    this.store
      .select(fromStore.selectBookingsState)
      .pipe(take(1))
      .subscribe((bookingsState) => {
        this.orderControl.patchValue(bookingsState[this.scope()].order);
      });
    this.page$ = this.store.select(fromStore.selectBookingsPage(this.scope()));
    this.collectionSize$ = this.store
      .select(fromStore.selectBookingsState)
      .pipe(
        map(
          (bookingsState: BookingsState) =>
            bookingsState[this.scope()].entitiesCount,
        ),
      );

    this.orderControl.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((order) =>
        this.store.dispatch(
          bookingsActions.setOrder({ order, scope: this.scope() }),
        ),
      );
  }

  selectRoomById(id: number): Signal<Room> {
    return this.store.selectSignal(selectRoomById(id));
  }

  isSameDayOrNextMorning(booking: Booking): boolean {
    return (
      (booking?.duration.rate === RateType.Daily &&
        booking.duration.quantity === 1) ||
      (booking?.duration.rate === RateType.Hourly &&
        booking.duration.quantity < 24)
    );
  }

  isNextMorning(booking: Booking): boolean {
    if (this.isSameDayOrNextMorning(booking)) {
      return !isSameDay(parseISO(booking.end), parseISO(booking.start));
    } else {
      return false;
    }
  }

  trackByBookingId = (booking: Booking): number => booking.id;

  changePage(page: number) {
    this.store.dispatch(bookingsActions.setPage({ page, scope: this.scope() }));
  }

  openBooking(booking: Booking) {
    const instance = this.modalService.open(BookingDetailsModalComponent, {});
    (instance.componentRef.instance as BookingDetailsModalComponent).bookingId =
      booking.id;
    (
      instance.componentRef.instance as BookingDetailsModalComponent
    ).displayCalendarButton = true;
  }

  roomsFilterChanged(values: number[]) {
    this.store.dispatch(
      bookingsActions.setRoomsFilter({
        filter: { ids: values },
        scope: this.scope(),
      }),
    );
  }

  statusFilterChanged(values: BookingStatus[]) {
    if (values.length === 0) {
      values = [BookingStatus.Pending, BookingStatus.Confirmed];
    }
    this.store.dispatch(
      bookingsActions.setBookingsFilter({
        filter: { status: values },
        scope: this.scope(),
      }),
    );
  }

  onFilterChanged(event: TableFilterEvent) {
    if (event.field === 'room_name') {
      this.roomsFilterChanged(event.values);
    } else if (event.field === 'status') {
      this.statusFilterChanged(event.values);
    }
  }

  onSortChanged(event: TableSortEvent) {
    const order = event.direction
      ? [{ field: event.field, order: event.direction as any }]
      : [];
    this.store.dispatch(
      bookingsActions.setOrder({ order, scope: this.scope() }),
    );
  }
}
